import { useState, useEffect, useRef } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { fmt } from '../../utils/format';
import { useApi } from '../../hooks/useApi';
import { Btn, useToast, Icon } from '../ui';
import { getAccessToken } from '../../config/auth';
import { parseLatLng } from '../../utils/helpers';
import { AnnotationPanel } from './MapAnnotations';
import { MapLayerCatalog } from './MapLayerCatalog';
import { BASEMAP_BY_ID, DEFAULT_BASEMAP_ID } from '../../constants/mapLayers';
import {
  applyBasemap, addOverlay, removeOverlay, setOverlayOpacity, reapplyOverlays,
  enableTerrain, disableTerrain, setTerrainExaggeration,
} from '../../utils/mapLayerEngine';

export const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';

// ---------------------------------------------------------------------------
// CDN loader helpers
// ---------------------------------------------------------------------------
function loadScript(src, onload, onerror) {
  if (document.querySelector(`script[src="${src}"]`)) { onload(); return; }
  const s = document.createElement('script');
  s.src = src; s.onload = onload;
  if (onerror) s.onerror = onerror;
  document.head.appendChild(s);
}
function loadLink(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement('link');
  l.rel = 'stylesheet'; l.href = href;
  document.head.appendChild(l);
}

const MB_VERSION  = '3.3.0';
const DRAW_VERSION = '1.4.3';
const MB_JS   = `https://cdn.jsdelivr.net/npm/mapbox-gl@${MB_VERSION}/dist/mapbox-gl.js`;
const MB_CSS  = `https://cdn.jsdelivr.net/npm/mapbox-gl@${MB_VERSION}/dist/mapbox-gl.css`;

// Ensure Mapbox GL JS + CSS are loaded from the CDN (same pattern the map init
// uses). Resolves once window.mapboxgl is available; rejects if the CDN load
// fails. Shared so other map surfaces (e.g. the prospect parcel picker) reuse
// exactly one loader instead of duplicating the script-injection dance.
export function ensureMapboxLoaded() {
  return new Promise((resolve, reject) => {
    if (window.mapboxgl) { resolve(); return; }
    loadLink(MB_CSS);
    loadScript(MB_JS, () => resolve(), () => reject(new Error('Failed to load Mapbox GL JS from the CDN.')));
  });
}
const DRAW_JS  = `https://cdn.jsdelivr.net/npm/@mapbox/mapbox-gl-draw@${DRAW_VERSION}/dist/mapbox-gl-draw.js`;
const DRAW_CSS = `https://cdn.jsdelivr.net/npm/@mapbox/mapbox-gl-draw@${DRAW_VERSION}/dist/mapbox-gl-draw.css`;

// Ensure Mapbox GL JS + the mapbox-gl-draw plugin are both loaded (GL first,
// then Draw). Resolves once window.MapboxDraw is available; rejects if either
// CDN load fails. Shared so any editable-map surface (e.g. FieldBoundaryEditor)
// reuses the exact same loader the parcels map uses.
export function ensureMapboxDrawLoaded() {
  return ensureMapboxLoaded().then(() => new Promise((resolve, reject) => {
    if (window.MapboxDraw) { resolve(); return; }
    loadLink(DRAW_CSS);
    loadScript(DRAW_JS, () => resolve(), () => reject(new Error('Failed to load Mapbox Draw from the CDN.')));
  }));
}

// KMZ/KML parsing libs (loaded on demand, same CDN pattern as Mapbox)
const JSZIP_VERSION     = '3.10.1';
const TOGEOJSON_VERSION = '5.8.1';
const JSZIP_JS     = `https://cdn.jsdelivr.net/npm/jszip@${JSZIP_VERSION}/dist/jszip.min.js`;
const TOGEOJSON_JS = `https://cdn.jsdelivr.net/npm/@tmcw/togeojson@${TOGEOJSON_VERSION}/dist/togeojson.umd.js`;

// De-dupe / selection key for a preview parcel feature
const featureKey = (props) => props?.regridLlRef || props?.regridId || '';

// Resolve the togeojson global regardless of UMD bundle naming
function getToGeoJSON() {
  const g = window.toGeoJSON || window.togeojson || window.tmcwTogeojson;
  return (g && typeof g.kml === 'function') ? g : null;
}

// Ensure JSZip + togeojson globals are available
function ensureKmzLibs() {
  return new Promise((resolve) => {
    const needZip = !window.JSZip;
    const needTg  = !getToGeoJSON();
    if (!needZip && !needTg) { resolve(); return; }
    let pending = (needZip ? 1 : 0) + (needTg ? 1 : 0);
    const done = () => { if (--pending <= 0) resolve(); };
    if (needZip) loadScript(JSZIP_JS, done);
    if (needTg)  loadScript(TOGEOJSON_JS, done);
  });
}

// Parse a .kmz (zipped) or .kml file into a GeoJSON FeatureCollection (client-side)
async function kmzToGeoJSON(file) {
  await ensureKmzLibs();
  const name = (file.name || '').toLowerCase();
  let kmlText;
  if (name.endsWith('.kml')) {
    kmlText = await file.text();
  } else {
    if (!window.JSZip) throw new Error('Unzip library failed to load.');
    const zip = await window.JSZip.loadAsync(file);
    const kmlEntry = Object.keys(zip.files).find(n => /\.kml$/i.test(n) && !zip.files[n].dir);
    if (!kmlEntry) throw new Error('No .kml file found inside the KMZ archive.');
    kmlText = await zip.files[kmlEntry].async('string');
  }
  const doc = new DOMParser().parseFromString(kmlText, 'text/xml');
  const tg  = getToGeoJSON();
  if (!tg) throw new Error('KML parser failed to load.');
  return tg.kml(doc) || { type: 'FeatureCollection', features: [] };
}

// KML often encodes parcel/tract boundaries as (closed) LineStrings rather than
// Polygons — e.g. CAD/Trimble survey exports. Promote closed rings to polygons so
// they work as area boundaries. Open lines (roads, ditches, connectors) are skipped.
function ringIsClosed(coords) {
  if (!Array.isArray(coords) || coords.length < 4) return false;
  const a = coords[0], b = coords[coords.length - 1];
  return Math.abs(a[0] - b[0]) < 1e-7 && Math.abs(a[1] - b[1]) < 1e-7;
}

// Connect open line segments end-to-end by shared endpoints, so a boundary
// exported as many disconnected segments can re-form into a single ring.
function stitchSegments(segments) {
  const near = (a, b) => Math.abs(a[0] - b[0]) < 1e-7 && Math.abs(a[1] - b[1]) < 1e-7;
  const rev  = (s) => [...s].reverse();
  const rem  = segments.map(s => s.slice());
  const chains = [];
  let guard = 0;
  while (rem.length && guard++ < 100000) {
    let chain = rem.shift().slice();
    let extended = true;
    while (extended) {
      extended = false;
      for (let i = 0; i < rem.length; i++) {
        const s = rem[i], head = chain[0], tail = chain[chain.length - 1];
        if      (near(tail, s[0]))            chain = chain.concat(s.slice(1));
        else if (near(tail, s[s.length - 1])) chain = chain.concat(rev(s).slice(1));
        else if (near(head, s[s.length - 1])) chain = s.slice(0, -1).concat(chain);
        else if (near(head, s[0]))            chain = rev(s).slice(0, -1).concat(chain);
        else continue;
        rem.splice(i, 1); extended = true; break;
      }
    }
    chains.push(chain);
  }
  return chains;
}

// Build usable area boundaries from a parsed KML/KMZ FeatureCollection.
// Polygons pass through; closed LineStrings become polygons; open line segments
// are stitched together and any resulting closed loop becomes a polygon.
function extractBoundaryPolygons(fc) {
  const polygons = [];
  const openSegments = [];
  const collectLine = (coords) => {
    const ring = (coords || []).map(c => [c[0], c[1]]); // drop elevation → 2D
    if (ringIsClosed(ring)) polygons.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } });
    else if (ring.length >= 2) openSegments.push(ring);
  };
  for (const f of (fc.features || [])) {
    const g = f.geometry;
    if (!g) continue;
    if (g.type === 'Polygon' || g.type === 'MultiPolygon') polygons.push(f);
    else if (g.type === 'LineString')                      collectLine(g.coordinates);
    else if (g.type === 'MultiLineString')                 (g.coordinates || []).forEach(collectLine);
  }
  let openLinesSkipped = 0;
  for (const chain of stitchSegments(openSegments)) {
    if (ringIsClosed(chain)) polygons.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [chain] } });
    else openLinesSkipped++;
  }
  return { polygons, openLinesSkipped };
}

// ---------------------------------------------------------------------------
// KMZ overlay layers — uploaded boundary + candidate parcels from area-query
// ---------------------------------------------------------------------------
function setupKmzLayers(map, boundaryFC, candidateFC) {
  ['kmz-candidates-selected','kmz-candidates-outline','kmz-candidates-fill','kmz-boundary-line'].forEach(id => {
    if (map.getLayer(id)) map.removeLayer(id);
  });
  ['kmz-candidates','kmz-boundary'].forEach(id => { if (map.getSource(id)) map.removeSource(id); });

  if (boundaryFC && boundaryFC.features?.length) {
    map.addSource('kmz-boundary', { type: 'geojson', data: boundaryFC });
    map.addLayer({ id: 'kmz-boundary-line', type: 'line', source: 'kmz-boundary',
      paint: { 'line-color': '#7c3aed', 'line-width': 2, 'line-dasharray': [2, 1] } });
  }
  if (candidateFC && candidateFC.features?.length) {
    map.addSource('kmz-candidates', { type: 'geojson', data: candidateFC });
    map.addLayer({ id: 'kmz-candidates-fill', type: 'fill', source: 'kmz-candidates',
      paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.22 } });
    map.addLayer({ id: 'kmz-candidates-outline', type: 'line', source: 'kmz-candidates',
      paint: { 'line-color': '#d97706', 'line-width': 1 } });
    map.addLayer({ id: 'kmz-candidates-selected', type: 'fill', source: 'kmz-candidates',
      filter: ['in', ['get', 'selKey'], ['literal', []]],
      paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.55 } });
  }
}

// ---------------------------------------------------------------------------
// Layer setup helpers (called once map is loaded and GeoJSON is ready)
// ---------------------------------------------------------------------------
function setupLayers(map, fc) {
  // Remove stale layers/sources from prior loads
  ['parcels-labels','parcels-fill-selected','parcels-outline','parcels-fill',
   'regrid-tiles-owner','regrid-tiles-apn','regrid-tiles-acreage',
   'regrid-tiles-landuse','regrid-tiles-assessed','regrid-tiles-zoning',
   'regrid-tiles-fill','regrid-tiles-outline'].forEach(id => {
    if (map.getLayer(id)) map.removeLayer(id);
  });
  ['parcels','regrid-tiles'].forEach(id => {
    if (map.getSource(id)) map.removeSource(id);
  });

  // ── Regrid background tile layer (all parcels in viewport) ──────────────
  map.addSource('regrid-tiles', {
    type:  'vector',
    tiles: [`${window.location.origin}/api/parcel-tiles/{z}/{x}/{y}`],
    minzoom: 12,
    maxzoom: 19,
  });
  map.addLayer({
    id: 'regrid-tiles-fill', type: 'fill', source: 'regrid-tiles',
    'source-layer': 'parcels',
    paint: { 'fill-color': '#94a3b8', 'fill-opacity': 0.08 },
  });
  // ── Toggleable color-fill layers ──────────────────────────────────────────
  map.addLayer({
    id: 'regrid-tiles-landuse', type: 'fill', source: 'regrid-tiles',
    'source-layer': 'parcels',
    layout: { visibility: 'none' },
    paint: {
      'fill-color': ['match', ['downcase', ['coalesce', ['get', 'usedesc'], '']],
        ['single family residential', 'residential', 'townhouse', 'condominium', 'mobile home', 'single family'], '#86efac',
        ['multi family residential', 'apartment', 'duplex', 'triplex', 'multi family'], '#4ade80',
        ['agricultural', 'cropland', 'pasture', 'farm', 'ranch', 'timber', 'agriculture'], '#a3e635',
        ['commercial', 'retail', 'office', 'hotel', 'restaurant', 'shopping'], '#fb923c',
        ['industrial', 'manufacturing', 'warehouse', 'utility', 'industrial'], '#94a3b8',
        ['vacant', 'vacant land', 'undeveloped', 'open land'], '#fbbf24',
        ['government', 'exempt', 'municipal', 'church', 'school', 'religious', 'institutional'], '#c084fc',
        '#e2e8f0',
      ],
      'fill-opacity': 0.45,
    },
  });
  map.addLayer({
    id: 'regrid-tiles-assessed', type: 'fill', source: 'regrid-tiles',
    'source-layer': 'parcels',
    layout: { visibility: 'none' },
    paint: {
      'fill-color': ['interpolate', ['linear'],
        ['coalesce', ['to-number', ['get', 'parval'], 0], 0],
        0,       '#f0f9ff',
        50000,   '#bae6fd',
        200000,  '#38bdf8',
        500000,  '#0284c7',
        1000000, '#01579b',
      ],
      'fill-opacity': 0.55,
    },
  });
  map.addLayer({
    id: 'regrid-tiles-zoning', type: 'fill', source: 'regrid-tiles',
    'source-layer': 'parcels',
    layout: { visibility: 'none' },
    paint: {
      'fill-color': ['match',
        ['slice', ['downcase', ['coalesce', ['get', 'zoning'], '']], 0, 1],
        'r', '#86efac',
        'c', '#fb923c',
        'i', '#94a3b8',
        'a', '#a3e635',
        'm', '#c084fc',
        '#e2e8f0',
      ],
      'fill-opacity': 0.45,
    },
  });
  map.addLayer({
    id: 'regrid-tiles-outline', type: 'line', source: 'regrid-tiles',
    'source-layer': 'parcels',
    paint: { 'line-color': '#94a3b8', 'line-width': 0.5 },
    minzoom: 14,
  });
  // ── Toggleable label layers ───────────────────────────────────────────────
  map.addLayer({
    id: 'regrid-tiles-owner', type: 'symbol', source: 'regrid-tiles',
    'source-layer': 'parcels',
    minzoom: 15,
    layout: {
      visibility: 'visible',
      'text-field': ['get', 'owner'],
      'text-size': 10,
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-anchor': 'center',
      'text-allow-overlap': false,
      'text-max-width': 8,
    },
    paint: {
      'text-color': '#334155',
      'text-halo-color': 'rgba(255,255,255,0.8)',
      'text-halo-width': 1,
    },
  });
  map.addLayer({
    id: 'regrid-tiles-apn', type: 'symbol', source: 'regrid-tiles',
    'source-layer': 'parcels',
    minzoom: 15,
    layout: {
      visibility: 'none',
      'text-field': ['get', 'parcelnumb'],
      'text-size': 9,
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-anchor': 'center',
      'text-allow-overlap': false,
      'text-max-width': 10,
    },
    paint: {
      'text-color': '#475569',
      'text-halo-color': 'rgba(255,255,255,0.8)',
      'text-halo-width': 1,
    },
  });
  map.addLayer({
    id: 'regrid-tiles-acreage', type: 'symbol', source: 'regrid-tiles',
    'source-layer': 'parcels',
    minzoom: 14,
    layout: {
      visibility: 'none',
      'text-field': ['case',
        ['>', ['coalesce', ['to-number', ['get', 'll_gisacre'], 0], 0], 0],
        ['concat',
          ['number-format', ['to-number', ['get', 'll_gisacre'], 0],
            { 'max-fraction-digits': 1 }],
          ' ac',
        ],
        '',
      ],
      'text-size': 10,
      'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
      'text-anchor': 'center',
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#334155',
      'text-halo-color': 'rgba(255,255,255,0.8)',
      'text-halo-width': 1,
    },
  });

  if (!fc || fc.features.length === 0) return;

  // ── Owned parcel layers ──────────────────────────────────────────────────
  map.addSource('parcels', { type: 'geojson', data: fc });

  map.addLayer({ id: 'parcels-fill', type: 'fill', source: 'parcels',
    paint: { 'fill-color': '#22d3ee', 'fill-opacity': 0.30 } });

  map.addLayer({ id: 'parcels-outline', type: 'line', source: 'parcels',
    paint: { 'line-color': '#0891b2', 'line-width': 2.5 } });

  // Selected highlight layer — filtered by parcelId via setFilter
  map.addLayer({ id: 'parcels-fill-selected', type: 'fill', source: 'parcels',
    filter: ['==', ['get', 'parcelId'], ''],
    paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.55 } });

  // Symbol layer — acreage label at polygon center
  map.addLayer({
    id: 'parcels-labels', type: 'symbol', source: 'parcels',
    minzoom: 14,
    layout: {
      'text-field': ['case',
        ['!=', ['coalesce', ['get', 'regridAcres'], ['get', 'acres']], null],
        ['concat',
          ['number-format', ['coalesce', ['get', 'regridAcres'], ['get', 'acres']],
            { 'max-fraction-digits': 1 }],
          ' ac',
        ],
        '',
      ],
      'text-size': 11,
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      'text-anchor': 'center',
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#1a3844',
      'text-halo-width': 1.5,
    },
  });
}

// ---------------------------------------------------------------------------
// Layer toggle definitions
// ---------------------------------------------------------------------------
const LAYER_TOGGLE_DEFS = [
  { key: 'ownerLabels',       id: 'regrid-tiles-owner',    label: 'Owner Names',            defaultOn: true  },
  { key: 'apnLabels',         id: 'regrid-tiles-apn',      label: 'Parcel Numbers (APN)',    defaultOn: false },
  { key: 'acreageLabels',     id: 'regrid-tiles-acreage',  label: 'Acreage',                defaultOn: false },
  { key: 'landUseFill',       id: 'regrid-tiles-landuse',  label: 'Land Use',               defaultOn: false },
  { key: 'assessedValueFill', id: 'regrid-tiles-assessed', label: 'Assessed Value',         defaultOn: false },
  { key: 'zoningFill',        id: 'regrid-tiles-zoning',   label: 'Zoning',                 defaultOn: false },
];
const DEFAULT_TOGGLES = Object.fromEntries(LAYER_TOGGLE_DEFS.map(d => [d.key, d.defaultOn]));

const LAND_USE_LEGEND = [
  { color: '#86efac', label: 'Residential (SF)' },
  { color: '#4ade80', label: 'Residential (MF)' },
  { color: '#a3e635', label: 'Agricultural' },
  { color: '#fb923c', label: 'Commercial' },
  { color: '#94a3b8', label: 'Industrial' },
  { color: '#fbbf24', label: 'Vacant' },
  { color: '#c084fc', label: 'Gov / Exempt' },
  { color: '#e2e8f0', label: 'Other' },
];
const ASSESSED_LEGEND = [
  { color: '#f0f9ff', label: '< $50k' },
  { color: '#bae6fd', label: '$50k – $200k' },
  { color: '#38bdf8', label: '$200k – $500k' },
  { color: '#0284c7', label: '$500k – $1M' },
  { color: '#01579b', label: '> $1M' },
];
const ZONING_LEGEND = [
  { color: '#86efac', label: 'R — Residential' },
  { color: '#fb923c', label: 'C — Commercial' },
  { color: '#94a3b8', label: 'I — Industrial' },
  { color: '#a3e635', label: 'A — Agricultural' },
  { color: '#c084fc', label: 'M — Mixed Use' },
  { color: '#e2e8f0', label: 'Other' },
];
const LAYER_LEGENDS = { landUseFill: LAND_USE_LEGEND, assessedValueFill: ASSESSED_LEGEND, zoningFill: ZONING_LEGEND };

function applyLayerVisibility(map, toggles) {
  LAYER_TOGGLE_DEFS.forEach(({ key, id }) => {
    if (map.getLayer(id))
      map.setLayoutProperty(id, 'visibility', toggles[key] ? 'visible' : 'none');
  });
}

// ---------------------------------------------------------------------------
// Full-detail JSON viewer — shows everything we have for a clicked parcel, not
// just the columns we extract. Parses any nested *Json strings carried in the
// feature properties (Mapbox serializes nested objects to strings) and pretty-
// prints the merged object.
// ---------------------------------------------------------------------------
function buildFullDetail(props) {
  if (!props) return null;
  const out = {};
  for (const [k, v] of Object.entries(props)) {
    if (v == null || v === '') continue;
    // Inline parsed copies of any JSON-string fields so the full upstream
    // payload is visible alongside the flat fields.
    if (/json$/i.test(k) && typeof v === 'string') {
      try { out[k.replace(/json$/i, '')] = JSON.parse(v); continue; } catch { /* keep raw string below */ }
    }
    out[k] = v;
  }
  return out;
}

const JsonBlock = ({ data }) => (
  <pre style={{
    margin: '8px 0 0', maxHeight: 260, overflow: 'auto', fontSize: 11, lineHeight: 1.5,
    background: '#0f172a', color: '#e2e8f0', borderRadius: 6, padding: '10px 12px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  }}>
    {JSON.stringify(data, null, 2)}
  </pre>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const ParcelsMap = ({ propertyID, loanID, dealDetailId, dealId, onExpandChange }) => {
  const { toast } = useToast();
  // dealId renders every parcel across a whole deal (Farm/land module). The
  // import / area-query tools require a single detail target and stay gated on
  // dealDetailId, so in deal-scope the map is read-only.
  const entityParam = dealDetailId ? `dealDetailId=${dealDetailId}`
                    : dealId       ? `dealId=${dealId}`
                    : propertyID   ? `propertyId=${propertyID}`
                    : `loanId=${loanID}`;
  const { data: parcels, reload } = useApi(() => api.get(`parcels?${entityParam}`), [propertyID, loanID, dealDetailId, dealId]);
  const mapContainer  = useRef(null);
  const mapRef        = useRef(null);
  const drawRef       = useRef(null);
  const [mapReady,     setMapReady]     = useState(false);
  const [selected,     setSelected]     = useState(null);       // parcel properties shown in panel
  const [importing,    setImporting]    = useState(false);      // direct "Import as Parcel" in flight

  // Focus mode — clicking the map enlarges it and hides the surrounding deal
  // detail (handled by the parent via onExpandChange).
  const [expanded,     setExpanded]     = useState(false);

  // Address search
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchResults, setSearchResults] = useState(null);  // FeatureCollection | null
  const [searching,     setSearching]     = useState(false);

  // Point identify (click on Regrid tile layer)
  const [identifyResult, setIdentifyResult] = useState(null);

  // Layer toggles (parcel-data layers on the Regrid tiles)
  const [layerToggles,   setLayerToggles]   = useState(DEFAULT_TOGGLES);
  const [showLayerPanel, setShowLayerPanel] = useState(false);

  // Basemap + overlay catalog state
  const [basemapId, setBasemapId] = useState(DEFAULT_BASEMAP_ID);
  const [overlays,  setOverlays]  = useState({});                       // { id: { overlay, on, opacity } }
  const [terrain,   setTerrain]   = useState({ on: false, exaggeration: 1.4 });

  // Refs mirror state so the post-basemap-swap rebuild reads fresh values.
  const lastFcRef          = useRef(null);
  const overlaysRef        = useRef(overlays);
  const terrainRef         = useRef(terrain);
  const layerTogglesRef    = useRef(layerToggles);
  const kmzBoundaryRef     = useRef(null);
  const previewFCRef       = useRef(null);
  const previewSelectedRef = useRef(null);
  const selectedRef        = useRef(null);
  overlaysRef.current     = overlays;
  terrainRef.current      = terrain;
  layerTogglesRef.current = layerToggles;

  // KMZ upload → tax parcel discovery + import
  const kmzInputRef = useRef(null);
  const [kmzBusy,         setKmzBusy]         = useState(false);
  const [kmzError,        setKmzError]        = useState(null);
  const [kmzBoundary,     setKmzBoundary]     = useState(null); // FeatureCollection of uploaded polygons
  const [previewFC,       setPreviewFC]       = useState(null); // candidate parcels from area-query
  const [previewMeta,     setPreviewMeta]     = useState(null);
  const [previewSelected, setPreviewSelected] = useState(() => new Set());
  const [importBusy,      setImportBusy]      = useState(false);

  // ── Map init ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current || !mapContainer.current) return;
    if (!MAPBOX_TOKEN) { setMapReady('no-token'); return; }

    const initDraw = (map) => {
      if (!window.MapboxDraw) return;
      const draw = new window.MapboxDraw({
        displayControlsDefault: false,
        controls: { polygon: true, trash: true },
      });
      map.addControl(draw, 'top-right');
      drawRef.current = draw;
    };

    const initMap = async () => {
      if (!mapContainer.current) return;
      try {
        const token = await getAccessToken();
        window.mapboxgl.accessToken = MAPBOX_TOKEN;
        const map = new window.mapboxgl.Map({
          container: mapContainer.current,
          style: BASEMAP_BY_ID[DEFAULT_BASEMAP_ID].style,
          zoom: 13,
          center: [-98.5, 39.5],
          transformRequest: (url) => {
            if (token && url.startsWith(window.location.origin + '/api/parcel-tiles/')) {
              return { url, headers: { Authorization: `Bearer ${token}` } };
            }
          },
        });
        map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
        map.addControl(new window.mapboxgl.FullscreenControl());
        map.on('load', () => {
          // Load Draw after map is ready
          if (window.MapboxDraw) {
            initDraw(map);
            setMapReady(true);
          } else {
            loadLink(DRAW_CSS);
            // Draw is optional — if its CDN load fails the map still works.
            loadScript(DRAW_JS, () => { initDraw(map); setMapReady(true); }, () => setMapReady(true));
          }
        });
        mapRef.current = map;
      } catch (err) {
        // new mapboxgl.Map() throws synchronously if the render worker can't be
        // created (e.g. a CSP that blocks its blob: worker) — surface it instead
        // of leaving a silent blank box.
        console.error('Mapbox failed to initialize:', err);
        setMapReady('load-error');
      }
    };

    if (window.mapboxgl) {
      initMap();
    } else {
      loadLink(MB_CSS);
      loadScript(MB_JS, initMap, () => {
        console.error('Failed to load Mapbox GL JS from the CDN.');
        setMapReady('load-error');
      });
    }

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  // ── Sync layer visibility when toggles change ─────────────────────────────
  useEffect(() => {
    if (!mapReady || mapReady === 'no-token' || !mapRef.current) return;
    applyLayerVisibility(mapRef.current, layerToggles);
  }, [layerToggles, mapReady]);

  // ── Focus mode: tell the parent to hide surrounding info, resize the map ───
  useEffect(() => {
    onExpandChange?.(expanded);
  }, [expanded, onExpandChange]);

  // The map container changes height when expanding/collapsing — Mapbox needs a
  // resize() once the new size is laid out, or it renders into the old box.
  useEffect(() => {
    if (!mapRef.current) return;
    const id = setTimeout(() => mapRef.current && mapRef.current.resize(), 260);
    return () => clearTimeout(id);
  }, [expanded]);

  // Leave focus mode when the component unmounts (e.g. tab switch) so the parent
  // doesn't stay collapsed.
  useEffect(() => () => { onExpandChange?.(false); }, [onExpandChange]);

  // ── Basemap / overlay / terrain controls ──────────────────────────────────
  // A basemap swap wipes every source+layer, so re-add overlays, terrain, the
  // Regrid tiles, owned parcels and KMZ overlays once the new style loads.
  // (map.on(...) handlers persist across setStyle, so they don't need re-binding.)
  const rebuildAfterStyle = () => {
    const map = mapRef.current;
    if (!map) return;
    reapplyOverlays(map, overlaysRef.current);
    if (terrainRef.current.on) enableTerrain(map, terrainRef.current.exaggeration);
    setupLayers(map, lastFcRef.current);
    applyLayerVisibility(map, layerTogglesRef.current);
    setupKmzLayers(map, kmzBoundaryRef.current, previewFCRef.current);
    if (previewSelectedRef.current && map.getLayer('kmz-candidates-selected'))
      map.setFilter('kmz-candidates-selected', ['in', ['get', 'selKey'], ['literal', [...previewSelectedRef.current]]]);
    const sel = selectedRef.current;
    if (sel && map.getLayer('parcels-fill-selected'))
      map.setFilter('parcels-fill-selected', ['==', ['get', 'parcelId'], sel.parcelId]);
  };

  const changeBasemap = (id) => {
    const map = mapRef.current;
    if (!map || id === basemapId) return;
    if (!applyBasemap(map, id)) return;
    setBasemapId(id);
    map.once('style.load', rebuildAfterStyle);
  };

  const toggleOverlay = (layer) => {
    const map = mapRef.current;
    setOverlays(prev => {
      const cur     = prev[layer.id];
      const on      = !cur?.on;
      const opacity = cur?.opacity ?? layer.defaultOpacity ?? 0.7;
      if (map) { if (on) addOverlay(map, layer, opacity); else removeOverlay(map, layer); }
      return { ...prev, [layer.id]: { overlay: layer, on, opacity } };
    });
  };

  const changeOverlayOpacity = (layer, opacity) => {
    const map = mapRef.current;
    if (map) setOverlayOpacity(map, layer, opacity);
    setOverlays(prev => ({ ...prev, [layer.id]: { ...prev[layer.id], overlay: layer, opacity } }));
  };

  const toggleTerrain = (on) => {
    const map = mapRef.current;
    if (map) { if (on) enableTerrain(map, terrain.exaggeration); else disableTerrain(map); }
    setTerrain(t => ({ ...t, on }));
  };

  const changeExaggeration = (exaggeration) => {
    const map = mapRef.current;
    if (map && terrain.on) setTerrainExaggeration(map, exaggeration);
    setTerrain(t => ({ ...t, exaggeration }));
  };

  // ── KMZ boundary + candidate parcel overlay ───────────────────────────────
  useEffect(() => {
    if (!mapReady || mapReady === 'no-token' || !mapRef.current) return;
    const map = mapRef.current;
    setupKmzLayers(map, kmzBoundary, previewFC);

    if (kmzBoundary?.features?.length) {
      const coords = [];
      kmzBoundary.features.forEach(f => {
        if (f.geometry?.type === 'Polygon')           coords.push(...f.geometry.coordinates[0]);
        else if (f.geometry?.type === 'MultiPolygon') f.geometry.coordinates.forEach(p => coords.push(...p[0]));
      });
      if (coords.length > 0) {
        const lngs = coords.map(c => c[0]), lats = coords.map(c => c[1]);
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 40, duration: 800 }
        );
      }
    }
  }, [kmzBoundary, previewFC, mapReady]);

  // ── Highlight selected candidate parcels on the map ───────────────────────
  useEffect(() => {
    if (!mapReady || mapReady === 'no-token' || !mapRef.current) return;
    const map = mapRef.current;
    if (map.getLayer('kmz-candidates-selected'))
      map.setFilter('kmz-candidates-selected', ['in', ['get', 'selKey'], ['literal', [...previewSelected]]]);
  }, [previewSelected, previewFC, mapReady]);

  // ── Add/update parcel layers when parcels + map are ready ─────────────────
  useEffect(() => {
    if (!mapReady || mapReady === 'no-token' || !mapRef.current || !parcels) return;
    const map = mapRef.current;

    // Register interaction handlers once — before the early return so that Regrid
    // tile clicks work even when no owned parcels are linked yet.
    if (!map._parcelHandlersRegistered) {
      map._parcelHandlersRegistered = true;

      // Click on owned parcel — select + flyTo
      map.on('click', 'parcels-fill', e => {
        const props = e.features[0]?.properties || null;
        selectParcel(props, map);
      });

      // Click a KMZ candidate parcel — toggle it in/out of the import selection
      // (mirrors the checklist below the map). Already-imported parcels are inert.
      map.on('click', 'kmz-candidates-fill', e => {
        const props = e.features?.[0]?.properties;
        if (!props || props.alreadyImported) return;
        const key = props.selKey;
        if (key) togglePreview(key);
      });

      // Click anywhere else — check Regrid tile layer via queryRenderedFeatures
      map.on('click', e => {
        // Any map click enters focus mode (enlarges map, hides surrounding info).
        setExpanded(true);

        const ownedHit = map.queryRenderedFeatures(e.point, { layers: ['parcels-fill'] });
        if (ownedHit.length > 0) return;

        // A candidate-parcel click is handled by its own toggle handler above —
        // don't also pop the Regrid identify panel for the tile underneath it.
        if (map.getLayer('kmz-candidates-fill')) {
          const candidateHit = map.queryRenderedFeatures(e.point, { layers: ['kmz-candidates-fill'] });
          if (candidateHit.length > 0) return;
        }

        const tileHit = map.queryRenderedFeatures(e.point, { layers: ['regrid-tiles-fill'] });
        if (tileHit.length > 0) {
          setIdentifyResult(tileHit[0].properties);
          setSelected(null);
        } else {
          setIdentifyResult(null);
        }
      });

      map.on('mouseenter', 'parcels-fill',        () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'parcels-fill',        () => { map.getCanvas().style.cursor = ''; });
      map.on('mouseenter', 'kmz-candidates-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'kmz-candidates-fill', () => { map.getCanvas().style.cursor = ''; });
      map.on('mouseenter', 'regrid-tiles-fill',   () => { map.getCanvas().style.cursor = 'crosshair'; });
      map.on('mouseleave', 'regrid-tiles-fill',   () => { map.getCanvas().style.cursor = ''; });
    }

    // Always attempt to load geometry — a parcel may have RegridGeometryJson even
    // if CentroidLat/CentroidLng weren't populated (which was the previous guard).
    if (parcels.length === 0) {
      lastFcRef.current = null;
      setupLayers(map, null);
      applyLayerVisibility(map, layerToggles);
      return;
    }

    api.get(`parcel-map?${entityParam}`).then(fc => {
      lastFcRef.current = fc;
      setupLayers(map, fc);
      applyLayerVisibility(map, layerToggles);

      if (!fc.features || fc.features.length === 0) {
        // No polygon data yet — fly to centroid if available so the user can see the area
        const withCentroid = parcels.filter(p => p.centroidLat && p.centroidLng);
        if (withCentroid.length > 0) {
          map.flyTo({ center: [withCentroid[0].centroidLng, withCentroid[0].centroidLat], zoom: 15, duration: 800 });
        }
        return;
      }

      // Fit bounds to polygon extents
      const coords = [];
      fc.features.forEach(f => {
        if (f.geometry?.type === 'Polygon')
          coords.push(...f.geometry.coordinates[0]);
        else if (f.geometry?.type === 'MultiPolygon')
          f.geometry.coordinates.forEach(p => coords.push(...p[0]));
      });
      if (coords.length > 0) {
        const lngs = coords.map(c => c[0]), lats = coords.map(c => c[1]);
        map.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 40, duration: 800 }
        );
      } else {
        // Polygon geometry exists but has no extractable coords — fly to centroid as fallback
        const withCentroid = parcels.filter(p => p.centroidLat && p.centroidLng);
        if (withCentroid.length > 0)
          map.flyTo({ center: [withCentroid[0].centroidLng, withCentroid[0].centroidLat], zoom: 15, duration: 800 });
      }
    }).catch(err => {
      console.error('parcel-map fetch failed:', err);
    });
  }, [mapReady, parcels]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parcel selection ──────────────────────────────────────────────────────
  const selectParcel = (props, map) => {
    setSelected(props);
    setIdentifyResult(null);
    if (!props) {
      if (map.getLayer('parcels-fill-selected'))
        map.setFilter('parcels-fill-selected', ['==', ['get', 'parcelId'], '']);
      return;
    }
    // Highlight selected parcel
    if (map.getLayer('parcels-fill-selected'))
      map.setFilter('parcels-fill-selected', ['==', ['get', 'parcelId'], props.parcelId]);
    // Fly to centroid
    if (props.centroidLat && props.centroidLng)
      map.flyTo({ center: [props.centroidLng, props.centroidLat], zoom: 15, duration: 800, essential: true });
  };

  // ── Parse Regrid path → { state, county } ────────────────────────────────
  const parseRegridPath = (path) => {
    const parts = (path || '').replace(/^\/us\//, '').split('/');
    const state  = parts[0]?.toUpperCase() || '';
    const county = (parts[1] || '').replace(/_/g, ' ')
      .split(' ').map(w => w ? w.charAt(0).toUpperCase() + w.slice(1) : '').join(' ').trim();
    return { state, county };
  };

  // ── Import a Regrid parcel directly as an owned parcel ───────────────────
  // (Identify a tile or address hit on the map, then create the record in one
  // click. The full add/edit form lives on the Parcels tab.)
  const importFromRegrid = async (result) => {
    if (!dealDetailId) { toast.error('No DealDetail context — cannot import parcel.'); return; }
    const isTile = !!result.parcelnumb; // tile hit vs address-search hit
    const { state: pathState, county: pathCounty } = parseRegridPath(result.path || '');

    // Tile hits don't include a `path` field — fall back to state2/scounty
    const state = isTile
      ? (pathState  || (result.state2  || '').toUpperCase())
      : (result.state  || '');
    const county = isTile
      ? (pathCounty || (result.scounty || '').replace(/ (County|Parish|Borough|Census Area|City and Borough|Municipality|Municipio)$/i, '').trim())
      : (result.county || '');
    const acres = isTile
      ? (result.ll_gisacre != null ? parseFloat(result.ll_gisacre)
         : result.gisacre  != null ? parseFloat(result.gisacre) : null)
      : (result.acres      != null ? parseFloat(result.acres)    : null);

    // parcelNumber = APN from county (displayed)
    // regridParcelId = Regrid internal ID (stored but not shown)
    const parcelNumber   = isTile ? (result.parcelnumb || '') : (result.parcelnumb || result.regridId || '');
    const regridParcelId = isTile ? (result.ll_uuid || '') : (result.regridLlRef || result.regridId || '');
    // taxParcelNumber = county/tax-office APN (often zero-padded), when Regrid
    // exposes an alternate/original form that differs from parcelnumb. Best-effort
    // — the auto-enrich below fills it authoritatively when it runs.
    const altApn = result.original_parcelnumb || result.alt_parcelnumb1 || result.alt_parcelnumb2 || result.parcelnumb_no_formatting || result.taxParcelNumber || null;
    const taxParcelNumber = altApn && altApn !== parcelNumber ? altApn : null;

    setImporting(true);
    try {
      const created = await api.post('parcels', {
        dealDetailId,
        parcelNumber:    parcelNumber   || null,
        taxParcelNumber: taxParcelNumber || null,
        regridParcelId:  regridParcelId || null,
        state:           state          || null,
        county:          county         || null,
        acres,
        legalDescription: isTile ? null : (result.legalDescription || null),
      });
      // Auto-enrich to pull boundary geometry when we have enough identifiers
      if (parcelNumber && state && county) {
        try { await api.post(`parcels/${created.parcelID}/enrich`, { parcelNumber, state, county }); }
        catch { /* enrich failure is non-fatal — parcel was still created */ }
      }
      toast.success('Parcel imported.');
      setIdentifyResult(null);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setImporting(false); }
  };

  // ── Parcel search (address or lat/long coordinates) ─────────────────────────
  // A query that parses as a lat/long pair (e.g. "32.7767, -96.797") does a
  // reverse point lookup; anything else is treated as an address search. Both
  // return a FeatureCollection the results dropdown can render uniformly.
  const runAddressSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const coords = parseLatLng(searchQuery);
      if (coords) {
        // Reverse lookup: the point endpoint returns a single Feature (or 404).
        const feature = await api.get(`parcel-lookup/point?lat=${coords.lat}&lng=${coords.lng}`);
        setSearchResults({ type: 'FeatureCollection', features: feature ? [feature] : [] });
      } else {
        const fc = await api.get(`parcel-lookup/address?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(fc);
      }
    } catch { setSearchResults({ type: 'FeatureCollection', features: [] }); }
    finally { setSearching(false); }
  };

  // Pick a search result → fly to it
  const pickSearchResult = (feature) => {
    const map = mapRef.current;
    const lat = feature.properties.centroidLat;
    const lng = feature.properties.centroidLng;
    if (map && lat && lng)
      map.flyTo({ center: [lng, lat], zoom: 16, duration: 900, essential: true });
    setIdentifyResult(feature.properties);
    setSearchResults(null);
    setSearchQuery('');
  };

  // ── KMZ upload → discover + import tax parcels ────────────────────────────
  const clearKmzPreview = () => {
    setKmzBoundary(null); setPreviewFC(null); setPreviewMeta(null);
    setKmzError(null); setPreviewSelected(new Set());
  };

  const onKmzFile = async (e) => {
    const file = e.target.files?.[0];
    if (kmzInputRef.current) kmzInputRef.current.value = ''; // allow re-selecting same file
    if (!file) return;
    if (!dealDetailId) { toast.error('No DealDetail context — cannot import parcels.'); return; }

    setKmzBusy(true); setKmzError(null);
    setKmzBoundary(null); setPreviewFC(null); setPreviewMeta(null); setPreviewSelected(new Set());
    try {
      const fc = await kmzToGeoJSON(file);
      const { polygons, openLinesSkipped } = extractBoundaryPolygons(fc);
      if (polygons.length === 0) {
        setKmzError(
          openLinesSkipped > 0
            ? `This file has ${openLinesSkipped} open line/path shape(s) but no closed boundary. Parcel boundaries must be polygons or closed outlines.`
            : 'No boundary polygons found in this file.'
        );
        return;
      }
      if (openLinesSkipped > 0)
        toast.info(`Using ${polygons.length} closed boundary shape(s); skipped ${openLinesSkipped} open line(s).`);
      const boundaryFC = { type: 'FeatureCollection', features: polygons };
      setKmzBoundary(boundaryFC);

      const result = await api.post('parcels/area-query', { geojson: boundaryFC, dealDetailId });
      const feats  = (result.features || []).map(f => ({
        ...f,
        properties: { ...f.properties, selKey: featureKey(f.properties) },
      }));
      setPreviewFC({ ...result, features: feats });
      setPreviewMeta(result.meta || null);
      setPreviewSelected(new Set(feats.filter(f => !f.properties.alreadyImported).map(f => f.properties.selKey)));

      const m = result.meta || {};
      if (feats.length === 0)            toast.info('No tax parcels intersect this boundary.');
      if (m.truncated)                   toast.info(`Showing the first ${feats.length} parcels — refine the boundary to narrow results.`);
      if (m.skipped?.length)             toast.error(`${m.skipped.length} boundary area(s) exceed 80 sq mi and were skipped. Split into smaller areas.`);
      if (m.ignoredGeometryTypes?.length) toast.info(`Ignored non-polygon shapes: ${m.ignoredGeometryTypes.join(', ')}.`);
    } catch (err) {
      setKmzError(err.message || 'Failed to read KMZ file.');
    } finally {
      setKmzBusy(false);
    }
  };

  const selectableKeys = () =>
    (previewFC?.features || []).filter(f => !f.properties.alreadyImported).map(f => f.properties.selKey);

  const togglePreview = (key) => {
    setPreviewSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const keys = selectableKeys();
    const all  = keys.length > 0 && keys.every(k => previewSelected.has(k));
    setPreviewSelected(all ? new Set() : new Set(keys));
  };

  const importSelected = async () => {
    if (!dealDetailId || !previewFC) return;
    const features = previewFC.features
      .filter(f => previewSelected.has(f.properties.selKey) && !f.properties.alreadyImported)
      .map(f => ({ geometry: f.geometry, properties: f.properties }));
    if (features.length === 0) { toast.error('Select at least one parcel to import.'); return; }

    setImportBusy(true);
    try {
      const res = await api.post('parcels/bulk-import', { dealDetailId, features });
      toast.success(
        `Imported ${res.imported} parcel${res.imported !== 1 ? 's' : ''}` +
        (res.skipped ? `, skipped ${res.skipped} duplicate${res.skipped !== 1 ? 's' : ''}` : '') + '.'
      );
      if (res.errors?.length) toast.error(`${res.errors.length} parcel(s) failed to import.`);
      clearKmzPreview();
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setImportBusy(false); }
  };

  // Keep rebuild refs current for the next basemap swap.
  selectedRef.current        = selected;
  kmzBoundaryRef.current     = kmzBoundary;
  previewFCRef.current        = previewFC;
  previewSelectedRef.current = previewSelected;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ marginTop: 8 }}>

      {/* ── Address search bar ── */}
      {MAPBOX_TOKEN && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runAddressSearch()}
            placeholder="Search parcel by address or lat, long…"
            style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${B.border}`, fontSize: 13 }}
          />
          <Btn size="sm" variant="secondary" disabled={searching || !searchQuery.trim()} onClick={runAddressSearch}>
            {searching ? '…' : 'Search'}
          </Btn>
        </div>
      )}

      {/* ── Address search results dropdown ── */}
      {searchResults && (
        <div style={{ background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 6, marginBottom: 10, fontSize: 12 }}>
          {searchResults.features.length === 0 ? (
            <div style={{ padding: '8px 12px', color: B.textLt }}>No parcels found.</div>
          ) : searchResults.features.map((f, i) => (
            <div key={i}
              onClick={() => pickSearchResult(f)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: i < searchResults.features.length - 1 ? `1px solid ${B.border}` : 'none' }}
            >
              <div style={{ fontWeight: 600 }}>{f.properties.siteAddress || f.properties.regridId}</div>
              <div style={{ color: B.textMd }}>
                {[f.properties.owner, f.properties.acres ? `${parseFloat(f.properties.acres).toFixed(2)} ac` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
          ))}
          <div style={{ padding: '4px 12px', textAlign: 'right' }}>
            <button onClick={() => setSearchResults(null)} style={{ fontSize: 11, color: B.textLt, background: 'none', border: 'none', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      )}

      {/* ── KMZ upload ── */}
      {dealDetailId && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <input
            ref={kmzInputRef}
            type="file"
            accept=".kmz,.kml"
            onChange={onKmzFile}
            style={{ display: 'none' }}
          />
          <Btn size="sm" variant="secondary" disabled={kmzBusy} onClick={() => kmzInputRef.current?.click()}>
            {kmzBusy ? 'Reading…' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="upload" size={13} /> Upload KMZ</span>}
          </Btn>
          <span style={{ fontSize: 11, color: B.textLt }}>
            Upload a KMZ/KML boundary to find every tax parcel inside it.
          </span>
          {(kmzBoundary || previewFC) && (
            <Btn size="sm" variant="ghost" onClick={clearKmzPreview}>Clear</Btn>
          )}
        </div>
      )}

      {kmzError && (
        <div style={{ background: B.red + '11', border: `1px solid ${B.red}`, borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: B.red }}>
          {kmzError}
        </div>
      )}

      {/* ── Map ── */}
      {!MAPBOX_TOKEN ? (
        <div style={{ padding: '20px', background: B.surfaceMuted, borderRadius: 8, marginBottom: 16, fontSize: 13, color: B.textMd }}>
          Set <code>REACT_APP_MAPBOX_TOKEN</code> in your environment to enable the map. The Parcels tab still works without it.
        </div>
      ) : (
        <div style={{ position: 'relative', height: expanded ? '78vh' : 360, marginBottom: 16, transition: 'height 0.2s ease' }}>
          <div ref={mapContainer} style={{ height: '100%', borderRadius: 8, overflow: 'hidden', border: `1px solid ${B.border}` }} />

          {mapReady === 'load-error' && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', background: B.surfaceMuted, borderRadius: 8, padding: 20 }}>
              <div style={{ textAlign: 'center', color: B.textMd, fontSize: 13, maxWidth: 380 }}>
                <div style={{ display: 'flex', justifyContent: 'center', color: B.textLt, marginBottom: 8 }}><Icon name="map" size={24} strokeWidth={1.75} /></div>
                <div style={{ fontWeight: 600, marginBottom: 4, color: B.text }}>The map couldn’t load.</div>
                Reload the page to try again. If it keeps happening, a browser extension or a content-security policy may be blocking Mapbox.
              </div>
            </div>
          )}

          {/* ── On-map toolbar (top-left): layers + land features ── */}
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 3, display: 'flex', flexDirection: 'column', gap: 8, width: 272, maxWidth: 'calc(100% - 20px)' }}>

            {/* Layer toggle panel */}
            <div>
              <button
                onClick={() => setShowLayerPanel(v => !v)}
                style={{ background: 'white', border: `1px solid ${B.border}`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', color: B.text }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name={showLayerPanel ? 'close' : 'layers'} size={13} /> Layers</span>
              </button>
              {showLayerPanel && (
                <div style={{ marginTop: 4, background: 'white', border: `1px solid ${B.border}`, borderRadius: 8, padding: '10px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', maxHeight: expanded ? '40vh' : 280, overflowY: 'auto', fontSize: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: B.text }}>Parcel Data</div>
                  {LAYER_TOGGLE_DEFS.map(({ key, label }) => (
                    <div key={key}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', cursor: 'pointer', color: B.text }}>
                        <input
                          type="checkbox"
                          checked={layerToggles[key]}
                          onChange={e => setLayerToggles(t => ({ ...t, [key]: e.target.checked }))}
                        />
                        {label}
                      </label>
                      {layerToggles[key] && LAYER_LEGENDS[key] && (
                        <div style={{ marginLeft: 22, marginBottom: 4 }}>
                          {LAYER_LEGENDS[key].map(({ color, label: lbl }) => (
                            <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '1px 0' }}>
                              <div style={{ width: 12, height: 12, borderRadius: 2, background: color, border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
                              <span style={{ color: B.textMd, fontSize: 11 }}>{lbl}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  <MapLayerCatalog
                    basemapId={basemapId}
                    onBasemapChange={changeBasemap}
                    overlays={overlays}
                    onToggleOverlay={toggleOverlay}
                    onOverlayOpacity={changeOverlayOpacity}
                    terrain={terrain}
                    onTerrainToggle={toggleTerrain}
                    onExaggerationChange={changeExaggeration}
                    hideHeader
                    style={{ width: 'auto', border: 'none', borderRadius: 0, padding: 0, overflow: 'visible', marginTop: 10, borderTop: `1px solid ${B.border}`, paddingTop: 8 }}
                  />
                </div>
              )}
            </div>

            {/* Land features toolbar (collapsed by default so it doesn't cover the map) */}
            {dealDetailId && (
              <div style={{ maxHeight: expanded ? '60vh' : 280, overflowY: 'auto', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                <AnnotationPanel
                  mapRef={mapRef}
                  drawRef={drawRef}
                  mapReady={mapReady}
                  ownerType="DealDetail"
                  ownerId={dealDetailId}
                  parcelDrawActive={false}
                  defaultCollapsed
                />
              </div>
            )}
          </div>

          {/* ── Enlarge / minimize map ── */}
          <button
            onClick={() => setExpanded(v => !v)}
            title={expanded ? 'Minimize map' : 'Enlarge map'}
            style={{ position: 'absolute', bottom: 10, right: 10, zIndex: 3, background: 'white', border: `1px solid ${B.border}`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.15)', color: B.text }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name={expanded ? 'minimize' : 'maximize'} size={13} /> {expanded ? 'Minimize' : 'Enlarge'}</span>
          </button>
        </div>
      )}

      {/* ── Selected owned parcel panel — full detail (everything we have) ── */}
      {selected && (
        <div style={{ background: B.surfaceMuted, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Parcel: {selected.parcelNumber || selected.regridLlRef || '—'}</div>
              <div style={{ fontSize: 11, color: B.textMd }}>
                {[selected.owner, [selected.county, selected.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
              </div>
            </div>
            <button onClick={() => setSelected(null)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.textMd, display: 'inline-flex' }}><Icon name="close" size={16} /></button>
          </div>
          <JsonBlock data={buildFullDetail(selected)} />
        </div>
      )}

      {/* ── Regrid tile identify panel (non-owned parcel click) — full detail ── */}
      {identifyResult && (
        <div style={{ background: '#f0f9ff', border: `1px solid #bae6fd`, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#0369a1' }}>Parcel Info (Regrid)</div>
              <div style={{ fontSize: 11, color: B.textMd }}>
                {[identifyResult.owner, identifyResult.parcelnumb || identifyResult.regridId].filter(Boolean).join(' · ')}
              </div>
            </div>
            <button onClick={() => setIdentifyResult(null)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.textMd, display: 'inline-flex' }}><Icon name="close" size={16} /></button>
          </div>
          <JsonBlock data={buildFullDetail(identifyResult)} />
          {dealDetailId && (
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <Btn size="sm" disabled={importing} onClick={() => importFromRegrid(identifyResult)}>
                {importing ? 'Importing…' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="download" size={13} /> Import as Parcel</span>}
              </Btn>
            </div>
          )}
        </div>
      )}

      {/* ── KMZ preview: candidate tax parcels ── */}
      {previewFC && (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ background: B.surfaceMuted, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: B.text }}>
              {previewFC.features.length} tax parcel{previewFC.features.length !== 1 ? 's' : ''} in boundary
              {previewMeta?.truncated && <span style={{ fontSize: 11, fontWeight: 400, color: B.textMd, marginLeft: 6 }}>(truncated — refine boundary)</span>}
              <div style={{ fontSize: 11, fontWeight: 400, color: B.textMd, marginTop: 2 }}>
                Click parcels on the map to toggle them, or use the list below.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: B.textMd }}>{previewSelected.size} selected</span>
              <Btn size="sm" variant="ghost" onClick={toggleSelectAll}>
                {selectableKeys().length > 0 && selectableKeys().every(k => previewSelected.has(k)) ? 'Deselect all' : 'Select all'}
              </Btn>
              <Btn size="sm" disabled={importBusy || previewSelected.size === 0} onClick={importSelected}>
                {importBusy ? 'Importing…' : `Import selected (${previewSelected.size})`}
              </Btn>
            </div>
          </div>

          {previewFC.features.length === 0 ? (
            <div style={{ padding: '16px 12px', fontSize: 12, color: B.textLt, textAlign: 'center' }}>
              No tax parcels intersect this boundary.
            </div>
          ) : (
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#fff', borderBottom: `1px solid ${B.border}`, textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px', width: 32 }} />
                    <th style={{ padding: '6px 8px', color: B.textMd }}>Owner</th>
                    <th style={{ padding: '6px 8px', color: B.textMd }}>APN</th>
                    <th style={{ padding: '6px 8px', color: B.textMd, textAlign: 'right' }}>Acres</th>
                    <th style={{ padding: '6px 8px', color: B.textMd, textAlign: 'right' }}>Assessed</th>
                    <th style={{ padding: '6px 8px', color: B.textMd }}>Zoning</th>
                    <th style={{ padding: '6px 8px', color: B.textMd }}>Land Use</th>
                  </tr>
                </thead>
                <tbody>
                  {previewFC.features.map((f, i) => {
                    const p   = f.properties;
                    const key = p.selKey;
                    const imported = p.alreadyImported;
                    return (
                      <tr key={key || i}
                        onClick={() => !imported && togglePreview(key)}
                        style={{
                          borderBottom: `1px solid ${B.border}`,
                          cursor: imported ? 'default' : 'pointer',
                          opacity: imported ? 0.5 : 1,
                          background: !imported && previewSelected.has(key) ? '#fff7ed' : 'transparent',
                        }}>
                        <td style={{ padding: '6px 8px' }}>
                          <input
                            type="checkbox"
                            disabled={imported}
                            checked={!imported && previewSelected.has(key)}
                            onChange={() => togglePreview(key)}
                            onClick={e => e.stopPropagation()}
                          />
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          {p.owner || '—'}
                          {imported && <span style={{ fontSize: 10, color: B.textLt, marginLeft: 6 }}>(imported)</span>}
                        </td>
                        <td style={{ padding: '6px 8px' }}>{p.regridId || '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{p.acres != null ? parseFloat(p.acres).toFixed(2) : '—'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{p.assessedValue != null ? fmt.currency(p.assessedValue) : '—'}</td>
                        <td style={{ padding: '6px 8px' }}>{p.zoning || '—'}</td>
                        <td style={{ padding: '6px 8px' }}>{p.landUse || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
};
